import { Component, Input, OnInit, inject } from '@angular/core';
import { DatePipe } from '@angular/common';
import { IActivo, IQueryTripero, Trip } from 'modelos/src';
import { IColumnas } from '../../../../../auxiliares/tabla/tabla.component';
import { HelperService } from '../../../../../auxiliares/servicios/helper.service';
import { DateRange } from '../../../../../auxiliares/date-range-selector/date-range-selector.component';
import { TriperoService } from '../../../../../auxiliares/servicios/http/tripero.service';

@Component({
  selector: 'app-listado-viajes',
  templateUrl: './listado-viajes.component.html',
  styleUrls: ['./listado-viajes.component.scss'],
  standalone: false,
})
export class ListadoViajesComponent implements OnInit {
  helper = inject(HelperService);
  private triperoService = inject(TriperoService);

  @Input() vehiculo?: IActivo;

  public loading = false;
  public viajes?: Trip[];
  public columnas: IColumnas<Trip>[] = [];
  public rango?: DateRange;

  public async listar(): Promise<void> {
    if (!this.vehiculo?._id || !this.rango) return;

    this.loading = true;
    this.viajes = undefined; // app-tabla muestra su loading mientras no hay datos
    try {
      const query: IQueryTripero = {
        idVehiculo: this.vehiculo._id,
        from: this.rango.start,
        to: this.rango.end,
      };
      const data = await this.triperoService.getTrips(query);
      this.viajes = data.sort((a, b) => {
        return a.startTime! > b.startTime! ? -1 : 1;
      });
    } catch (error) {
      console.error('Error al obtener viajes:', error);
      this.helper.notifError('Error al obtener viajes');
    }
    this.loading = false;
  }

  public async onRangeChange(rango: DateRange): Promise<void> {
    this.rango = rango;
    await this.listar();
  }

  private setColumnas(): void {
    this.columnas = [
      {
        header: { label: 'Inicio' },
        row: {
          fecha: (dato) => (dato.startTime ? new Date(dato.startTime) : null),
          noData: 'Sin fecha',
        },
      },
      {
        header: { label: 'Fin' },
        row: {
          fecha: (dato) => (dato.endTime ? new Date(dato.endTime) : null),
          noData: 'Sin fecha',
        },
      },
      {
        header: { label: 'Duración' },
        row: {
          // Tripero devuelve duración en segundos
          parse: (dato) =>
            dato.duration != null
              ? this.helper.parseSeconds(dato.duration)
              : undefined,
          noData: 'Sin duración',
        },
      },
      {
        header: { label: 'Distancia' },
        row: {
          parse: (dato) =>
            dato.distance != null
              ? `${this.helper.mToKm(dato.distance, true).toLocaleString('es-AR')} km`
              : undefined,
          noData: 'Sin distancia',
        },
      },
      {
        header: { label: 'Vel. máx.' },
        row: {
          // Tripero devuelve velocidad en km/h
          parse: (dato) =>
            dato.maxSpeed != null
              ? `${Math.floor(dato.maxSpeed).toLocaleString('es-AR')} km/h`
              : undefined,
          noData: 'Sin velocidad',
        },
      },
      {
        header: { label: 'Vel. prom.' },
        row: {
          // Tripero devuelve velocidad en km/h
          parse: (dato) =>
            dato.averageSpeed != null
              ? `${Math.floor(dato.averageSpeed).toLocaleString('es-AR')} km/h`
              : undefined,
          noData: 'Sin velocidad',
        },
      },
      {
        header: { label: 'Origen' },
        row: {
          parse(dato) {
            return HelperService.extraerDireccion(dato.startAddress);
          },
          tooltip(dato) {
            return dato.startAddress; // Tooltip con la dirección completa
          },
          noData: 'Sin dirección',
        },
      },
      {
        header: { label: 'Destino' },
        row: {
          parse(dato) {
            return HelperService.extraerDireccion(dato.endAddress);
          },
          tooltip(dato) {
            return dato.endAddress; // Tooltip con la dirección completa
          },
          noData: 'Sin dirección',
        },
      },
    ];
  }

  async ngOnInit(): Promise<void> {
    this.setColumnas();

    // Rango por defecto: últimos 2 días
    const to = new Date();
    const from = new Date(to);
    from.setDate(from.getDate() - 2);

    this.rango = {
      start: from.toISOString(),
      end: to.toISOString(),
    };

    await this.listar();
  }
}
